import _ from 'lodash';

import { RESULT_GROUP_DIVIDER } from 'constants/documentExtractionFields';
import { DOC_FIELD_TYPES, dropDownDocumentFieldTypes } from 'constants/documentFieldTypes';
import { flattenExtractionFieldDefinitionTree } from 'utils/aiExtractionModels';
import { getConditionalArray } from 'utils/data';
import { getOptionDocumentExtracionFieldFromOption } from 'utils/documentExtraction';

import { formatConfidencePercent } from '../../AiBatchIntake/AiBatchIntake.utils';

import { EXTRACTION_ROW_FRAME_KIND } from './DocumentExtractionFieldsTab.constants';

/**
 * Returns true when the confidence value is below the backend-provided threshold.
 * Both confidence and threshold may be 0–100 or 0.0–1.0; since they come from the
 * same backend they share the same scale, so the raw comparison is valid.
 *
 * @param {number|null|undefined} confidence
 * @param {number|null|undefined} threshold
 * @returns {boolean}
 */
export const checkIsLowConfidence = (confidence, threshold) => {
  if (_.isNil(confidence) || _.isNil(threshold)) {
    return false;
  }

  return confidence < threshold;
};

/**
 * Reorders extraction fields so the ones below their review threshold surface
 * first, preserving the original relative order within each group (stable
 * partition). Fields with no confidence/threshold count as not-low and stay
 * after the flagged ones. Used by the "Low confidence first" review toggle so a
 * reviewer can triage the fields that need attention without scrolling past the
 * confident ones; the default order (untouched) is the field definitions' order.
 *
 * @param {Array<{ confidence: number|null|undefined, threshold: number|null|undefined }>} fields
 * @returns {Array<Object>} the same fields, low-confidence ones first
 */
export const sortFieldsLowConfidenceFirst = fields => {
  if (_.isEmpty(fields)) {
    return fields;
  }

  const low = fields.filter(field => checkIsLowConfidence(field.confidence, field.threshold));
  const rest = fields.filter(field => !checkIsLowConfidence(field.confidence, field.threshold));

  return [...low, ...rest];
};

/**
 * The confidence pill text: the plain percentage for a healthy result, or the
 * below-threshold callout ("58% · below this field's 80% threshold") when the
 * result fell under its per-field review threshold.
 *
 * @param {{ confidence: number|null|undefined, threshold: number|null|undefined }} params
 * @returns {string}
 */
export const getConfidencePillText = ({ confidence, threshold }) => {
  if (checkIsLowConfidence(confidence, threshold)) {
    return `${formatConfidencePercent(confidence)} · below this field's ${formatConfidencePercent(
      threshold,
    )} threshold`;
  }

  return formatConfidencePercent(confidence);
};

/**
 * Converts a flat array of extraction field definitions (as returned by the API with
 * resultStructure=FlatValues) into the nested tree shape expected by getExtractionFieldsTree.
 *
 * Each node in the result has { id, name, parentId, orderNumber, documentFieldType, Children[] }.
 * Root nodes are those whose parentId is null/undefined/empty.
 *
 * @param {Array<{
 *   id: string,
 *   name: string,
 *   parentId: string|null,
 *   orderNumber: number,
 *   documentFieldType: { id: string, typeDefinition: { type: string } },
 * }>} flatFields - flat list of field definitions from the API
 * @returns {Array<Object>} root nodes with nested Children arrays
 */
export const buildNestedExtractionFields = flatFields => {
  if (_.isEmpty(flatFields)) {
    return [];
  }

  const nodeMap = {};

  flatFields.forEach(field => {
    nodeMap[field.id] = { ...field, Children: [] };
  });

  const roots = [];

  flatFields.forEach(field => {
    const node = nodeMap[field.id];
    const isRoot = _.isNil(field.parentId) || field.parentId === '';

    if (isRoot) {
      roots.push(node);

      return;
    }

    const parent = nodeMap[field.parentId];

    if (parent) {
      parent.Children.push(node);

      return;
    }

    roots.push(node);
  });

  const sortByOrder = nodes => {
    nodes.sort((a, b) => a.orderNumber - b.orderNumber);
    nodes.forEach(n => sortByOrder(n.Children));

    return nodes;
  };

  return sortByOrder(roots);
};

// Aliased re-export so this module's own callers (buildExtractionFieldTypeMap below, and
// DocumentExtractionFieldsTab's locate-control join) don't each hold a duplicate copy —
// utils/aiExtractionModels is the canonical source (needed by the store selector layer too,
// which can't import from a component-local utils file).
export const flattenExtractionFieldDefinitions = flattenExtractionFieldDefinitionTree;

/**
 * Build a map of documentExtractionFieldId → field type ('Text' | 'Bool' | ...), used to
 * coerce raw extraction result values into the shape the edit form expects (notably Bool,
 * whose stored "True"/"False" string is truthy and would render every checkbox checked).
 *
 * @param {{
 *   extractionFields: Array<Object>|null|undefined,   // nested defs embedded on document.Type
 *   inspectorFlatFields: Array<Object>|null|undefined, // flat defs loaded by the inspector fallback
 * }} params
 * @returns {Record<string, string>} field id → typeDefinition.type
 */
export const buildExtractionFieldTypeMap = ({ extractionFields, inspectorFlatFields }) => {
  const resolvedFields = !_.isEmpty(extractionFields)
    ? flattenExtractionFieldDefinitions(extractionFields)
    : (inspectorFlatFields ?? []);

  return resolvedFields.reduce((acc, field) => {
    const type = field?.documentFieldType?.typeDefinition?.type;

    if (_.isNil(field?.id) || _.isNil(type)) {
      return acc;
    }

    acc[field.id] = type;

    return acc;
  }, {});
};

const checkIsTruthyExtractionValue = value => {
  if (_.isBoolean(value)) {
    return value;
  }

  if (_.isString(value)) {
    return value.trim().toLowerCase() === 'true';
  }

  return !!value;
};

/**
 * Build a map of documentExtractionFieldId → its option list (id/name pairs), for
 * SingleSelectionDropDown field definitions. A dropdown's selectable options are NOT
 * nested `Children` records — they live as a flat string array on
 * `documentFieldType.options` (e.g. `["Disbursement", "Down Payment", "Other"]`), the
 * same shape EditExtractionFieldsFormFieldsPart's own tree-building step
 * (getDocumentExtractionFieldsWithNamedType) synthesizes into option nodes via
 * getOptionDocumentExtracionFieldFromOption — reused here so the id scheme
 * (`${documentFieldType.id}-${optionName}`) matches exactly what that editor's
 * `nestedFields` will contain. Used to resolve a raw extracted option NAME (the only
 * shape the extraction result stores) back to that option id, so the edit form's
 * dropdown can preselect the document's existing value instead of opening blank.
 *
 * @param {{
 *   extractionFields: Array<Object>|null|undefined,   // nested defs embedded on document.Type
 *   inspectorFlatFields: Array<Object>|null|undefined, // flat defs loaded by the inspector fallback
 * }} params
 * @returns {Record<string, Array<{ id: string, name: string }>>}
 */
export const buildDropdownOptionsMap = ({ extractionFields, inspectorFlatFields }) => {
  const resolvedFields = !_.isEmpty(extractionFields)
    ? extractionFields
    : buildNestedExtractionFields(inspectorFlatFields ?? []);

  return flattenExtractionFieldDefinitions(resolvedFields).reduce((acc, field) => {
    const type = field?.documentFieldType?.typeDefinition?.type;
    const options = field?.documentFieldType?.options;

    if (
      type !== DOC_FIELD_TYPES.SINGLE_SELECTION_DROP_DOWN ||
      _.isNil(field?.id) ||
      _.isEmpty(options)
    ) {
      return acc;
    }

    acc[field.id] = options.map(optionName =>
      getOptionDocumentExtracionFieldFromOption({
        typeParentId: field.documentFieldType.id,
        name: optionName,
        parentId: field.id,
      }),
    );

    return acc;
  }, {});
};

/**
 * Maps the document's existing extraction results into the edit form's initial values, keyed
 * by documentExtractionFieldId (the form key the base field tree uses). Prefilling is what makes
 * "Edit all" open with the current values — the override endpoint replaces the ENTIRE result set,
 * so an empty form would drop every unedited field (and 400 on any that is required).
 *
 * Bool values are coerced to a real boolean (the stored "True"/"False" string is otherwise
 * truthy). SingleSelectionDropDown values are resolved from their extracted option NAME to the
 * matching option's `{ id }` via dropdownOptionsById, so the dropdown preselects the document's
 * current value instead of opening blank. MultipleSelectionDropDown is still skipped — its form
 * value is an array shape a raw string cannot satisfy — so it stays at its empty default rather
 * than seed a malformed value.
 *
 * @param {{
 *   extractionFieldsResults: Array<{ documentExtractionFieldId: string, value: * }>|null|undefined,
 *   fieldTypeById: Record<string, string>,
 *   dropdownOptionsById?: Record<string, Array<{ id: string, name: string }>>,
 * }} params
 * @returns {Record<string, *>} form id → prefilled value
 */
export const buildExtractionResultsInitialForm = ({
  extractionFieldsResults,
  fieldTypeById,
  dropdownOptionsById = {},
}) => {
  if (_.isEmpty(extractionFieldsResults)) {
    return {};
  }

  return extractionFieldsResults.reduce((acc, result) => {
    const { documentExtractionFieldId, value } = result ?? {};

    if (_.isNil(documentExtractionFieldId)) {
      return acc;
    }

    const fieldType = fieldTypeById?.[documentExtractionFieldId] ?? null;

    if (fieldType === DOC_FIELD_TYPES.SINGLE_SELECTION_DROP_DOWN) {
      const matchedOption = (dropdownOptionsById[documentExtractionFieldId] ?? []).find(
        option => _.toLower(option.name) === _.toLower(value),
      );

      if (matchedOption) {
        acc[documentExtractionFieldId] = { id: matchedOption.id, name: matchedOption.name };
      }

      return acc;
    }

    if (dropDownDocumentFieldTypes.includes(fieldType)) {
      return acc;
    }

    acc[documentExtractionFieldId] =
      fieldType === DOC_FIELD_TYPES.BOOL ? checkIsTruthyExtractionValue(value) : value;

    return acc;
  }, {});
};

/**
 * Entity-type vocabulary: maps the dot-path prefix from DocumentTypeFieldsMapping.propertyPath
 * (e.g. "income.monthlyIncome") to the association key used in the document associations store
 * (e.g. "incomeIds"), the options key in selectDocumentAssociationsMulti (e.g. "incomes"),
 * and a human-readable label.
 */
const PROPERTY_PATH_ENTITY_MAP = {
  borrower: { associationKey: 'borrowerIds', optionsKey: 'borrowers', label: 'Borrower' },
  coborrower: { associationKey: 'borrowerIds', optionsKey: 'borrowers', label: 'Co-borrower' },
  income: { associationKey: 'incomeIds', optionsKey: 'incomes', label: 'Income' },
  asset: { associationKey: 'assetIds', optionsKey: 'assets', label: 'Asset' },
  property: { associationKey: 'propertyIds', optionsKey: 'properties', label: 'Property' },
  liability: { associationKey: 'liabilityIds', optionsKey: 'liabilities', label: 'Liability' },
};

/**
 * Extract the entity type config from a dot-separated property path.
 * Returns null when the path prefix is not in the known entity vocabulary.
 *
 * @param {string|null|undefined} propertyPath - e.g. "income.monthlyIncome"
 * @returns {{ associationKey: string, optionsKey: string, label: string }|null}
 */
export const resolveEntityTypeFromPath = propertyPath => {
  if (_.isNil(propertyPath) || !propertyPath.includes('.')) {
    return null;
  }

  const prefix = propertyPath.split('.')[0].toLowerCase();

  return PROPERTY_PATH_ENTITY_MAP[prefix] ?? null;
};

/**
 * Extract the leaf field name (last segment) from a dot-separated property path.
 *
 * @param {string|null|undefined} propertyPath - e.g. "income.monthlyIncome"
 * @returns {string|null}
 */
export const resolveFieldNameFromPath = propertyPath => {
  if (_.isNil(propertyPath) || !propertyPath.includes('.')) {
    return null;
  }

  return _.last(propertyPath.split('.')) ?? null;
};

/**
 * Build a map of documentFieldTypeId → { linkedEntityId, entityLabel, fieldLabel }
 * for use by the DocumentExtractionFieldsTab to populate the apply-bar on each field.
 *
 * Only populates for fields where:
 *  - there is a mapping entry (propertyPath is set)
 *  - the entity type is recognised
 *  - exactly ONE entity of that type is linked to the document
 *
 * @param {{
 *   fieldsMapping: Record<string, { documentFieldTypeId: string, propertyPath: string }>,
 *   documentAssociations: {
 *     borrowerIds: string[],
 *     incomeIds: string[],
 *     assetIds: string[],
 *     propertyIds: string[],
 *     liabilityIds: string[],
 *   },
 *   associationOptions: {
 *     borrowers: { value: string, name: string }[],
 *     incomes: { value: string, name: string }[],
 *     assets: { value: string, name: string }[],
 *     properties: { value: string, name: string }[],
 *     liabilities: { value: string, name: string }[],
 *   },
 * }} params
 * @returns {Record<string, { linkedEntityId: string, linkedEntityLabel: string, linkedEntityFieldLabel: string, entityType: string, linkedEntityBorrowerId: string|null }>}
 */
export const buildFieldApplyMap = ({ fieldsMapping, documentAssociations, associationOptions }) => {
  if (_.isEmpty(fieldsMapping) || _.isEmpty(documentAssociations)) {
    return {};
  }

  return Object.values(fieldsMapping).reduce((acc, { documentFieldTypeId, propertyPath }) => {
    const entityTypeConfig = resolveEntityTypeFromPath(propertyPath);

    if (_.isNil(entityTypeConfig)) {
      return acc;
    }

    const { associationKey, optionsKey, label } = entityTypeConfig;
    const linkedIds = documentAssociations[associationKey] ?? [];

    if (linkedIds.length !== 1) {
      return acc;
    }

    const linkedEntityId = linkedIds[0];
    const entityOptions = associationOptions[optionsKey] ?? [];
    const entityOption = entityOptions.find(o => o.value === linkedEntityId);

    // Income edits require the owning borrowerId (the edit endpoint is nested under a borrower).
    // If we can't resolve it, don't offer apply for that income — a write would build a bad URL.
    const linkedEntityBorrowerId = entityOption?.borrowerId ?? null;

    if (associationKey === 'incomeIds' && _.isNil(linkedEntityBorrowerId)) {
      return acc;
    }

    acc[documentFieldTypeId] = {
      linkedEntityId,
      linkedEntityLabel: entityOption ? entityOption.name : label,
      linkedEntityFieldLabel: resolveFieldNameFromPath(propertyPath),
      entityType: associationKey,
      linkedEntityBorrowerId,
    };

    return acc;
  }, {});
};

/**
 * Marks, for every row, which of its frames open and close on it. A frame is one of the boxes a
 * field sits inside — the card a top-level container is drawn in, the container box itself, a
 * result group: the flattened list draws each as a top segment carrying the label, side segments,
 * and a closing bottom segment, so a nested table keeps the boxes it had before virtualisation.
 *
 * @param {Array<object>} rows
 * @returns {Array<object>}
 */
const markFrameEdges = rows =>
  rows.map((row, index) => ({
    ...row,
    frames: row.frames.map((frame, depth) => ({
      ...frame,
      isFirst: rows[index - 1]?.frames[depth]?.key !== frame.key,
      isLast: rows[index + 1]?.frames[depth]?.key !== frame.key,
    })),
  }));

/**
 * Flattens the extraction field tree into one row per rendered field, each carrying the frames it
 * sits inside. A credit report holds thousands of table rows, and rendering them all at once is
 * what makes the tab freeze.
 *
 * @param {Array<object>} fields
 * @param {Array<{ key: string, kind: string, name: string|null }>} frames
 * @returns {Array<{ key: string, frames: Array<object>, field: object }>}
 */
export const flattenFieldTreeRows = (fields, frames = []) =>
  (fields ?? []).flatMap(field => {
    if (field.type !== DOC_FIELD_TYPES.CONTAINER || _.isNil(field.nestedGroups)) {
      return [{ key: field.nodeId ?? field.id, frames, field }];
    }

    const groupIds = Object.keys(field.nestedGroups);
    const containerKey = field.nodeId ?? field.id;

    const containerFrames = [
      ...frames,
      ...getConditionalArray(_.isEmpty(frames), () => [
        { key: `${containerKey}-card`, kind: EXTRACTION_ROW_FRAME_KIND.CARD, name: null },
      ]),
      { key: containerKey, kind: EXTRACTION_ROW_FRAME_KIND.BOX, name: field.name },
    ];

    const rows = groupIds.flatMap(groupId => {
      const groupFrames =
        groupIds.length === 1
          ? containerFrames
          : [
              ...containerFrames,
              {
                key: `${containerKey}${RESULT_GROUP_DIVIDER}${groupId}`,
                kind: EXTRACTION_ROW_FRAME_KIND.BOX,
                name: null,
              },
            ];

      return flattenFieldTreeRows(field.nestedGroups[groupId], groupFrames).map(row => ({
        ...row,
        key: `${groupId}${RESULT_GROUP_DIVIDER}${row.key}`,
      }));
    });

    return markFrameEdges(rows);
  });

/**
 * Walks up from an element to the first ancestor that scrolls; react-virtualized needs that element
 * because the tab itself does not own the scrollbar — the inspector panel above it does.
 *
 * @param {HTMLElement|null} element
 * @returns {HTMLElement|null}
 */
export const findScrollParent = element => {
  if (_.isNil(element)) {
    return null;
  }

  const { overflowY } = window.getComputedStyle(element);

  return overflowY === 'auto' || overflowY === 'scroll'
    ? element
    : findScrollParent(element.parentElement);
};
